export type AppActivationStatus =
  "trial" | "active_included" | "active_paid" | "expired";

export interface AppTrial {
  id: string;
  expiresAt?: string | null;
}

export interface App {
  id: string;
  name: string;
  description: string | null;
  url: string | null;
  ownerEmail: string | null;
  ownerFirstName: string | null;
  ownerLastName: string | null;
  useCase: string | null;
  client: Client;
  createdAt: string;
  activationStatus?: AppActivationStatus;
  activatedAt?: string | null;
  trial?: AppTrial | null;
  /** Whether the current user may log in to the tool (member of its login group). */
  hasAccess?: boolean;
}

export interface Client {
  id: string;
  name: string;
  companyName: string | null;
  description: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  createdAt: string;
  apps: App[];
  language: string | null;
}

export type UserRole = "Owner" | "Admin" | "User";

export const USER_ROLE = {
  OWNER: "Owner" as const,
  ADMIN: "Admin" as const,
  USER: "User" as const,
} as const;

export interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: UserRole;
  // optional mfa status provided by the backend
  mfaStatus?: UserMfaStatus;
}

export type PlanKey = "basic" | "advanced" | "professional";

export interface Plan {
  key: PlanKey;
  name: string;
  basePriceEur: number;
  includedTools: number;
  userLimit: number;
}

export type SubscriptionStatus =
  | "active"
  | "pending_change"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "cancel_scheduled";

export interface Subscription {
  id: string;
  planKey: PlanKey;
  requestedPlanKey?: PlanKey | null;
  status: SubscriptionStatus;
  /**
   * Set once cancellation is scheduled (`status === 'cancel_scheduled'`,
   * #2786): the Stripe-confirmed date the subscription will actually end,
   * ISO 8601. Lets `PricingTab` show "active until {date}" (#2796). `null`
   * otherwise.
   */
  cancelAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Stripe invoice status, as returned by `GET /subscriptions/invoices`. */
export type InvoiceStatus = "paid" | "open" | "uncollectible" | "void";

/**
 * A single invoice entry returned by `GET /subscriptions/invoices` (#2791),
 * shown by `InvoiceHistoryList` on the Pricing/Billing tab (#2794). `pdfUrl`
 * is this app's own signed, short-lived redirect link — never a direct
 * Stripe URL — so it is always safe to render as a plain `<a>` download
 * link, including for a read-only Admin viewer.
 */
export interface Invoice {
  id: string;
  date: string;
  amountEur: number;
  currency: string;
  status: InvoiceStatus | string;
  pdfUrl: string;
}

/**
 * Response of `POST /subscriptions` (first-time plan selection, #2792): the
 * local `Subscription` is created in `incomplete` status (not `active`) and
 * `clientSecret` is the Stripe PaymentIntent's client secret, used to mount
 * Stripe Elements and confirm payment client-side (see
 * `stripe-checkout-form.tsx`). Never contains raw card data.
 */
export interface SelectPlanResult {
  subscription: Subscription;
  clientSecret: string;
}

export interface SeatInfo {
  planKey: PlanKey | null;
  userLimit: number;
}

/**
 * Response of `GET /subscriptions/upgrade-preview` (#2787/#2798): the exact,
 * Stripe-computed prorated charge an upgrade to `planKey` would bill
 * immediately if confirmed right now. Per the UX/UI design spec on #2785
 * ("show the exact charge ... do not hallucinate"), the frontend must only
 * ever display `amountDueEur` as given — never compute/estimate its own
 * number.
 */
export interface UpgradePreview {
  planKey: PlanKey;
  amountDueEur: number;
  currency: string;
}

export interface BillingAddress {
  id?: string;
  companyName: string;
  vatId?: string | null;
  line1: string;
  line2?: string | null;
  postalCode: string;
  city: string;
  country: string;
}

export const ADDITIONAL_TOOL_PRICE_EUR = 249;

export type UserMfaStatus =
  "not-required" | "pending-invite" | "required-not-configured" | "configured";

export type MfaPolicy = "off" | "totp-required";
